import { useMemo } from 'react';
import { BiSolidDownArrow, BiSolidUpArrow } from 'react-icons/bi';
import { useMutation, useQueryClient } from 'react-query';

import { Button, Loader, Modal } from '@/components';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import useToast from '@/hooks/useToast';
import { postChangeTeam } from '@/services/team11';
import { TeamActionTypes } from '@/store';
import { RequestChangeTeam } from '@/types/team';

export const ModalChangeConfirm = () => {
  const toast = useToast();
  const { state, dispatch } = useTeamStore();
  const playersMaps = usePlayerTemplateMap();
  const queryClient = useQueryClient();

  const { mutateAsync: onSubmitChange, isLoading } = useMutation(
    async (dataChange: RequestChangeTeam) => await postChangeTeam(dataChange),
    {
      onSuccess: (data) => {
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            showModalChangePlayer: false,
            selectedPlayerOnTeam: null,
            playerDetailsModalOpen: false,
            ...(data.estado && { team: data.equipo?.jugadores }),
          },
        });

        // Invalidar la consulta useGetCurrentTeamF11 para actualizar los datos en ModalChangeSuggestion
        queryClient.invalidateQueries('useGetCurrentTeamF11');
        if (data.estado) {
          toast.show(data.mensaje);
        } else {
          toast.show(data.mensaje);
        }
      },
    }
  );

  const playerSelectedIsTitular = useMemo(() => {
    if (state.selectedPlayerOnTeam && 'titular' in state.selectedPlayerOnTeam) {
      return state.selectedPlayerOnTeam.titular;
    }
  }, [state.selectedPlayerOnTeam]);

  const mappedPlayer = useMemo(() => {
    if (state.selectedPlayerOnTeam && state.selectedPlayerOnTeam?.id)
      return playersMaps.get(state.selectedPlayerOnTeam?.id);
  }, [playersMaps, state.selectedPlayerOnTeam]);

  const playerIncoming = useMemo(() => {
    if (
      !playerSelectedIsTitular &&
      state.team &&
      mappedPlayer &&
      mappedPlayer.p !== 1
    ) {
      return playersMaps.get(state.team[mappedPlayer.p - 1].id);
    } else if (
      !playerSelectedIsTitular &&
      state.team &&
      mappedPlayer &&
      mappedPlayer.p === 1
    ) {
      return mappedPlayer;
    } else {
      if (state.team && mappedPlayer) {
        return playersMaps.get(state.team[mappedPlayer.p + 10].id);
      }
    }
  }, [mappedPlayer, playerSelectedIsTitular, playersMaps, state.team]);

  const playerOutgoing = useMemo(() => {
    if (playerSelectedIsTitular) {
      return mappedPlayer;
    } else {
      if (state.team && mappedPlayer) {
        return playersMaps.get(state.team[mappedPlayer.p - 1].id);
      }
    }
  }, [mappedPlayer, playerSelectedIsTitular, playersMaps, state.team]);

  const contentModal = useMemo(() => {
    return (
      <div className="lg:w-[352px] lg:mx-auto mt-6 lg:mt-9">
        <div className="flex bg-gdt-status-green-light rounded text-gdt-status-green h-11 px-4 items-center">
          <div className="flex items-center flex-1">
            <BiSolidUpArrow className="mr-3" />
            <strong className="text-xs">ENTRA</strong>
          </div>
          <p className="text-sm font-bold">
            {playerIncoming?.a}, {playerIncoming?.n}
          </p>
        </div>
        <div className="flex bg-gdt-status-red-light rounded text-gdt-status-red h-11 px-4 items-center mt-3">
          <p className="flex-1 text-sm font-bold">
            {playerOutgoing?.a}, {playerOutgoing?.n}
          </p>
          <div className="flex items-start">
            <strong className="text-xs">SALE</strong>
            <BiSolidDownArrow className="ml-3" />
          </div>
        </div>
        <div className="pb-10 lg:pb-0 text-center">
          <Button
            className="w-full mt-6 lg:mt-8 h-12 lg:w-[256px]"
            disabled={isLoading}
            type="button"
            variant="primary"
            onClick={() => {
              if (playerIncoming?.id && playerOutgoing?.id) {
                onSubmitChange({
                  jugador_entra: playerIncoming.id,
                  jugador_sale: playerOutgoing.id,
                });
              }
            }}
          >
            Aceptar
          </Button>
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, playerIncoming]);
  return (
    <>
      {<Loader isVisible={isLoading} timeout={true} />}
      <Modal
        className="pt-7 px-0 !max-w-[426px]"
        controlledOnClose={() =>
          dispatch({
            type: TeamActionTypes.SET_STATE,
            payload: {
              showModalChangePlayer: false,
              selectedPlayerOnTeam: null,
              playerDetailsModalOpen: false,
            },
          })
        }
        isControlledOpen={Boolean(state.showModalChangePlayer)}
        render={() => {
          return contentModal;
        }}
        size="xs"
        title="Confirmar cambio"
        variant="controlled"
      />
    </>
  );
};
